import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "../../../App.css";
import { useAuth } from "../../../context/AuthContext";
import adminIds from "../../../analytics";

import atomicMailImage from "../../../assets/tools/atomicmail.png";
import obsidianImage from "../../../assets/tools/obsidian.png";
import hcodxImage from "../../../assets/tools/hcodx.png";
import htmlCheatImage from "../../../assets/tools/htmlcheat.png";
import pythonCheatImage from "../../../assets/tools/pythoncheat.png";
import asciiCheatImage from "../../../assets/tools/asciicheat.png";
import expressImage from "../../../assets/tools/express.png";
import audiomassImage from "../../../assets/tools/audiomass.png";
import converterImage from "../../../assets/tools/converter.png";
import compressorImage from "../../../assets/tools/compressor.png";
import virusTotalImage from "../../../assets/tools/virustotal.png";
import wolframImage from "../../../assets/tools/wolfram.png";
import mangoolsImage from "../../../assets/tools/mangools.png";
import typingImage from "../../../assets/tools/typing.png";
import labexImage from "../../../assets/tools/labex.png";
import ciscoImage from "../../../assets/tools/cisco.png";
import osintImage from "../../../assets/tools/osint.png";
import shodanImage from "../../../assets/tools/shodan.png";
import cyberChefImage from "../../../assets/tools/cyberchef.png";

const toolSections = [
  {
    key: "create",
    kicker: "CREATE",
    title: "Creation Tools",
    text: "Build, edit, package, and generate creative or technical assets.",
    tools: [
      {
        title: "ATOMIC MAIL",
        url: "https://atomicmail.io",
        image: atomicMailImage,
      },
      {
        title: "OBSIDIAN",
        url: "https://obsidian.md",
        image: obsidianImage,
      },
      {
        title: "HCODX",
        url: "https://hcodx.com",
        image: hcodxImage,
      },
      {
        title: "HTML CHEAT",
        url: "https://htmlcheatsheet.com",
        image: htmlCheatImage,
      },
      {
        title: "PYTHON CHEAT",
        url: "https://cs.valdosta.edu/~dgibson/courses/python/beginners_python_cheat_sheet_pcc_all.pdf",
        image: pythonCheatImage,
      },
      {
        title: "ASCII CHEAT",
        url: "https://www.asciiart.eu/",
        image: asciiCheatImage,
      },
      {
        title: "ADOBE EXPRESS",
        url: "https://www.adobe.com/uk/express",
        image: expressImage,
      },
      {
        title: "AUDIOMASS",
        url: "https://audiomass.co",
        image: audiomassImage,
      },
      {
        title: "CONVERTER",
        url: "https://www.online-convert.com",
        image: converterImage,
      },
      {
        title: "COMPRESSOR",
        url: "https://www.compress2go.com",
        image: compressorImage,
      },
    ],
  },
  {
    key: "search",
    kicker: "SEARCH",
    title: "Search Tools",
    text: "Lookup, verify, calculate, and inspect resources before use.",
    tools: [
      {
        title: "VIRUS TOTAL",
        url: "https://www.virustotal.com/gui/home/upload",
        image: virusTotalImage,
      },
      {
        title: "WOLFRAM ALPHA",
        url: "https://www.wolframalpha.com",
        image: wolframImage,
      },
      {
        title: "MANGOOLS",
        url: "https://mangools.com",
        image: mangoolsImage,
      },
    ],
  },
  {
    key: "learn",
    kicker: "LEARN",
    title: "Learning Tools",
    text: "Beginner friendly educational tools.",
    tools: [
      {
        title: "TYPING",
        url: "https://www.typing.com",
        image: typingImage,
      },
      {
        title: "LABEX",
        url: "https://labex.io/skilltrees",
        image: labexImage,
      },
      {
        title: "CISCO SKILLS",
        url: "https://www.netacad.com",
        image: ciscoImage,
      },
    ],
  },
  {
    key: "operate",
    kicker: "OPERATE",
    title: "Operation Tools",
    text: "Professional reconnaissance and cyber-intelligence tools.",
    tools: [
      {
        title: "OSINT FRAMEWORK",
        url: "https://osintframework.com",
        image: osintImage,
      },
      {
        title: "SHODAN",
        url: "https://www.shodan.io",
        image: shodanImage,
      },
      {
        title: "CYBER CHEF",
        url: "https://gchq.github.io/CyberChef",
        image: cyberChefImage,
      },
    ],
  },
];

const ToolCard = ({ title, image, url, status = "TOOL" }) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="tools-card-link"
      aria-label={`Open ${title} in a new window`}
    >
      <div className="tools-card">
        <div className="tools-card-kicker">{status}</div>
        <div className="tools-card-image-frame">
          <img src={image} alt={`${title} logo`} className="tools-card-image" />
        </div>
        <h3 className="tools-card-title">{title}</h3>
      </div>
    </a>
  );
};

const Tools = () => {
  const { user } = useAuth();

  const isOffice = useMemo(() => {
    return !!(user && adminIds.includes(user.id));
  }, [user]);

  const displayName = user?.global_name || user?.username || "Operator";

  return (
    <div className="profile-shell">
      <div className="profile-panel tools-panel">
        <div className="profile-topbar">
          <div>
            <div className="profile-kicker">POGFORTS TOOLBOX</div>
            <h1 className="profile-title">{displayName}</h1>
            <h2 className="profile-subtitle">
              Free tools for the things we learn in Pogforts.
            </h2>
          </div>

          <div className="profile-top-actions">
            <Link to="/online/profile" className="profile-admin-button">
              PROFILE
            </Link>

            {isOffice && (
              <Link to="/analytics" className="profile-admin-button">
                ADMIN
              </Link>
            )}
          </div>
        </div>

        {toolSections.map((section) => (
          <div
            key={section.key}
            className={`tools-section tools-section-${section.key}`}
          >
            <div className="tools-section-header">
              <div className="tools-section-kicker">{section.kicker}</div>
              <h3 className="tools-section-title">{section.title}</h3>
              <p className="tools-section-text">{section.text}</p>
            </div>

            <div className="tools-grid">
              {section.tools.map((tool) => (
                <ToolCard
                  key={`${section.key}-${tool.title}`}
                  title={tool.title}
                  image={tool.image}
                  url={tool.url}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Tools;
